// Detects when a new build has been deployed and moves the user onto it.
//
// The server sends index.html without a Cache-Control header, so browsers
// can keep showing an old build for hours after a deploy. Admins also leave
// tabs open for days. Both mean fixes look like they "did not work".
//
// Each build's index.html loads js/app.<hash>.js. We compare the hash this
// page loaded with the hash in a fresh copy of index.html.

const APP_RE = /\bjs\/app\.([0-9a-f]+)\.js/;
const CHECK_EVERY_MS = 10 * 60 * 1000;

let stale = false;
let banner = null;

function loadedHash() {
  for (const s of document.scripts) {
    const m = s.src && s.src.match(APP_RE);
    if (m) return m[1];
  }
  return null;
}

// cache: "reload" skips the browser cache and stores the fresh copy, so the
// next normal page load gets the new index.html too.
async function fetchFresh(path) {
  const res = await fetch(path, { cache: "reload", credentials: "same-origin" });
  return res.text();
}

async function check() {
  const current = loadedHash();
  if (!current || stale) return;
  try {
    const html = await fetchFresh("/");
    const m = html.match(APP_RE);
    if (m && m[1] !== current) {
      stale = true;
      showBanner();
    }
  } catch (e) {
    // Offline or blocked: try again next time
  }
}

function showBanner() {
  if (banner) return;
  banner = document.createElement("div");
  banner.setAttribute("role", "status");
  banner.style.cssText =
    "position:fixed;left:50%;bottom:20px;transform:translateX(-50%);" +
    "z-index:100000;background:#222;color:#fff;padding:12px 16px;" +
    "border-radius:6px;font-size:14px;display:flex;gap:12px;" +
    "align-items:center;box-shadow:0 2px 8px rgba(0,0,0,.3);max-width:90vw";
  const text = document.createElement("span");
  text.textContent = "A new version of the site is available.";
  const button = document.createElement("button");
  button.textContent = "Refresh";
  button.style.cssText =
    "background:#fff;color:#222;border:0;border-radius:4px;" +
    "padding:6px 12px;cursor:pointer;font-weight:600";
  button.addEventListener("click", () => window.location.reload());
  banner.appendChild(text);
  banner.appendChild(button);
  document.body.appendChild(banner);
}

async function hardNavigate(path) {
  try {
    await fetchFresh(path);
  } catch (e) {
    // Navigate anyway
  }
  window.location.assign(path);
}

export default function setupBuildCheck(router) {
  if (process.env.NODE_ENV !== "production" || !loadedHash()) return;

  let pendingPath = null;

  // Switching pages is a safe moment to load the new build: nothing on the
  // current page is lost that a normal page change would not lose anyway.
  router.beforeEach((to, from, next) => {
    pendingPath = to.fullPath;
    if (stale && from.matched.length > 0) {
      hardNavigate(to.fullPath);
      return next(false);
    }
    next();
  });

  // A page chunk from the old build was deleted by the deploy
  router.onError((err) => {
    if (/Loading (CSS )?chunk/i.test(String(err && err.message))) {
      hardNavigate(pendingPath || window.location.pathname);
    }
  });

  check();
  setInterval(check, CHECK_EVERY_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") check();
  });
}
